import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Crown, Send } from "lucide-react";
import { getCreatorProfile } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import ThemeToggle from "../components/ThemeToggle";
import AuthButton from "../components/AuthButton";
import { STYLE_PACKS } from "../lib/stylePacks";

/**
 * CreatorProfile — per-creator public page.
 *
 * Renders the creator's signature backdrop as a massive hero, their
 * latest public cards as a grid below, and a "send them a card back"
 * CTA that pre-fills the home reply field with the freshest card's slug.
 *
 * Returns 404 (via API) if the creator hasn't opted in to the gallery
 * — keeps non-public profiles unreachable by direct URL.
 */
export default function CreatorProfile() {
    const navigate = useNavigate();
    const { creatorId } = useParams();
    const { user } = useAuth();
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const data = await getCreatorProfile(creatorId, 12);
                setProfile(data);
            } catch (e) {
                setError(e?.response?.status === 404 ? "not_found" : "error");
            }
        })();
    }, [creatorId]);

    const bgUrl = profile?.signature_backdrop_url;
    const absBg = bgUrl?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${bgUrl}` : bgUrl;
    const latest = profile?.recent_cards?.[0];

    return (
        <div className="min-h-screen bg-app text-app" data-testid="creator-profile-page">
            <header className="sticky top-0 z-40 backdrop-blur-md bg-app/80 border-b border-app/15">
                <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
                    <Link
                        to="/creators"
                        data-testid="creator-profile-back-link"
                        className="btn-press inline-flex items-center gap-2 text-app-2 hover:text-app font-sans text-xs uppercase tracking-[0.3em] transition-colors"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                        <span>all creators</span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <ThemeToggle />
                        <AuthButton />
                    </div>
                </div>
            </header>

            {error === "not_found" && (
                <main className="max-w-2xl mx-auto px-5 py-20 text-center" data-testid="creator-profile-404">
                    <p className="font-serif italic text-app text-2xl mb-2">no creator at this address.</p>
                    <p className="font-sans text-app-3 text-sm mb-6">
                        Either they've never set a signature backdrop, or they've turned off public discovery.
                    </p>
                    <button
                        type="button"
                        onClick={() => navigate("/creators")}
                        className="btn-press inline-flex items-center gap-2 px-4 py-2 rounded-full border border-app/20 text-app-2 hover:text-app hover:border-app/45 font-sans text-[10px] uppercase tracking-[0.32em]"
                    >
                        back to creators
                    </button>
                </main>
            )}

            {error === "error" && (
                <main className="max-w-2xl mx-auto px-5 py-20 text-center" data-testid="creator-profile-error">
                    <p className="font-sans text-app-3 text-sm">couldn't load this creator. please try again.</p>
                </main>
            )}

            {!error && !profile && (
                <main className="max-w-2xl mx-auto px-5 py-20 text-center">
                    <p className="font-sans text-app-3 text-xs uppercase tracking-[0.3em]">loading…</p>
                </main>
            )}

            {profile && (
                <>
                    {/* Hero — signature backdrop fills the screen with the name overlaid. */}
                    <section
                        className="relative w-full"
                        style={{ minHeight: "60vh" }}
                        data-testid="creator-profile-hero"
                    >
                        <div
                            className="absolute inset-0"
                            style={{
                                background: `linear-gradient(180deg, rgba(0,0,0,0.20) 0%, rgba(0,0,0,0.65) 80%, var(--app-bg, #0c0a08) 100%), url("${absBg}") center/cover no-repeat, #0c0a08`,
                            }}
                        />
                        <div className="relative max-w-5xl mx-auto px-5 py-20 lg:py-28">
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7 }}
                                className="text-white"
                            >
                                <div className="inline-flex items-center gap-2 mb-3">
                                    <Crown className="w-3.5 h-3.5 text-amber-200/85" strokeWidth={1.5} />
                                    <span className="font-sans text-[10px] uppercase tracking-[0.42em] text-white/80">
                                        creator
                                    </span>
                                </div>
                                <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl leading-tight mb-3 drop-shadow-md">
                                    {profile.name?.toLowerCase()}
                                </h1>
                                {/* Magazine-masthead signature — renders the creator's signature
                                    line in their brand color (slot 1, the same one the cards
                                    themselves use). Sets the editorial tone before the work below. */}
                                {profile.signature_line && (
                                    <p
                                        data-testid="creator-profile-signature-line"
                                        className="font-serif italic text-lg sm:text-xl leading-snug mb-3 drop-shadow"
                                        style={{
                                            color: profile.brand_colors?.[1]
                                                ? `color-mix(in srgb, ${profile.brand_colors[1]} 60%, #f8e8c8 40%)`
                                                : "#f8e8c8",
                                            opacity: 0.95,
                                        }}
                                    >
                                        {profile.signature_line}
                                    </p>
                                )}
                                {/* Top-lane caption — tells visitors what kind of voice they're
                                    walking into BEFORE they scan cards. Shows only when the
                                    creator has 3+ lane-tagged cards in a single dominant lane. */}
                                {profile.top_lane && (
                                    <p
                                        data-testid="creator-profile-top-lane"
                                        className="font-sans text-[10px] uppercase tracking-[0.32em] text-white/70 mb-3"
                                    >
                                        writing in the <span className="text-white">{profile.top_lane}</span> lane mostly
                                    </p>
                                )}
                                <p className="font-sans text-sm text-white/85 max-w-md">
                                    every card below carries this backdrop. {profile.total_cards} public card{profile.total_cards === 1 ? "" : "s"} so far.
                                </p>

                                {latest?.slug && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            try {
                                                window.sessionStorage.setItem("moodcard.reply_from_slug", latest.slug);
                                            } catch { /* ignore */ }
                                            navigate("/");
                                        }}
                                        data-testid="creator-profile-send-back-button"
                                        className="btn-press mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-amber-300/55 bg-amber-400/15 text-amber-100 hover:bg-amber-400/25 backdrop-blur-sm font-sans text-[10px] uppercase tracking-[0.3em] transition-colors"
                                    >
                                        <Send className="w-3 h-3" strokeWidth={1.6} />
                                        <span>send {profile.name?.split(" ")[0]?.toLowerCase() || "them"} a card back</span>
                                    </button>
                                )}
                            </motion.div>
                        </div>
                    </section>

                    {/* Recent cards grid. */}
                    <main className="max-w-5xl mx-auto px-5 py-12">
                        <div className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3 mb-5">
                            recent cards
                        </div>
                        {profile.recent_cards.length === 0 ? (
                            <p className="font-serif italic text-app-3 text-sm" data-testid="creator-profile-empty-cards">
                                no public cards yet — check back soon.
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="creator-profile-grid">
                                {profile.recent_cards.map((card, i) => {
                                    const packLabel = STYLE_PACKS[card.pack]?.label || card.pack;
                                    const cardCustomBg = card.custom_background_url;
                                    const cardBgAbs = cardCustomBg?.startsWith("/")
                                        ? `${process.env.REACT_APP_BACKEND_URL || ""}${cardCustomBg}`
                                        : cardCustomBg;
                                    const previewCard = (card.cards || [])[0];
                                    return (
                                        <Link
                                            key={card.id}
                                            to={`/c/${card.slug}`}
                                            data-testid={`creator-card-tile-${i}`}
                                            className="rounded-2xl overflow-hidden border border-app/20 bg-surface-1 hover-tilt block"
                                            style={{ aspectRatio: "3 / 4" }}
                                        >
                                            <div
                                                className="w-full h-full relative"
                                                style={cardBgAbs ? {
                                                    background: `linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.65) 100%), url("${cardBgAbs}") center/cover no-repeat, #0c0a08`,
                                                } : {
                                                    background: `linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.55) 100%), url("${absBg}") center/cover no-repeat, #0c0a08`,
                                                }}
                                            >
                                                <div className="absolute top-3 left-3">
                                                    <span className="font-sans text-[9px] uppercase tracking-[0.32em] text-white/85 px-2 py-1 rounded-full bg-black/30 backdrop-blur-sm">
                                                        {packLabel?.toLowerCase()}
                                                    </span>
                                                </div>
                                                <div className="absolute bottom-4 left-4 right-4">
                                                    <p className="font-serif italic text-white text-base leading-snug mb-2 line-clamp-3 drop-shadow-md">
                                                        {previewCard?.body || card.feeling}
                                                    </p>
                                                    <span className="font-sans text-[9px] uppercase tracking-[0.3em] text-white/70">
                                                        {new Date(card.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </main>
                </>
            )}
        </div>
    );
}
